import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

export type WizardPage =
  | 'org-units'
  | 'migration-progress'
  | 'site-mapping'
  | 'group-reconciliation'
  | 'test-deployment'
  | 'deployment'
  | 'post-deployment'
  | 'platform-groups';

interface WizardContextType {
  currentPage: WizardPage;
  sitesAssigned: number;
  sitesTotal: number;
  groupsConfirmed: boolean;
  testConductedDate: Date | null;
  deploymentScheduled: boolean;
  deploymentDate: Date | null;
  pendingNavTarget: WizardPage | null;
  testStats: { running: number; completed: number };
  navigateTo: (page: WizardPage) => void;
  setNavGuard: (guard: (() => boolean) | null) => void;
  proceedNav: () => void;
  cancelNav: () => void;
  reportSiteProgress: (assigned: number, total: number) => void;
  confirmGroups: () => void;
  recordTestDeployment: (date: Date) => void;
  scheduleDeployment: (date: Date) => void;
  updateTestStats: (running: number, completed: number) => void;
  resetProgress: () => void;
}

const VALID_PAGES: WizardPage[] = [
  'org-units', 'migration-progress', 'site-mapping', 'group-reconciliation',
  'test-deployment', 'deployment', 'post-deployment', 'platform-groups',
];

const hashPage = (): WizardPage => {
  const h = window.location.hash.slice(1) as WizardPage;
  return VALID_PAGES.includes(h) ? h : 'migration-progress';
};

const WizardContext = createContext<WizardContextType | null>(null);

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentPage, setCurrentPage] = useState<WizardPage>(hashPage);
  const [sitesAssigned, setSitesAssigned] = useState(0);
  const [sitesTotal, setSitesTotal] = useState(0);
  const [groupsConfirmed, setGroupsConfirmed] = useState(false);
  const [testConductedDate, setTestConductedDate] = useState<Date | null>(null);
  const [deploymentScheduled, setDeploymentScheduled] = useState(false);
  const [deploymentDate, setDeploymentDate] = useState<Date | null>(null);
  const [pendingNavTarget, setPendingNavTarget] = useState<WizardPage | null>(null);
  const [testStats, setTestStats] = useState({ running: 0, completed: 0 });

  const navGuardRef = useRef<(() => boolean) | null>(null);

  const setNavGuard = useCallback((guard: (() => boolean) | null) => {
    navGuardRef.current = guard;
  }, []);

  const navigateTo = useCallback((page: WizardPage) => {
    if (navGuardRef.current && !navGuardRef.current()) {
      setPendingNavTarget(page);
      return;
    }
    window.location.hash = page;
    setCurrentPage(page);
  }, []);

  const proceedNav = useCallback(() => {
    if (!pendingNavTarget) return;
    navGuardRef.current = null;
    window.location.hash = pendingNavTarget;
    setCurrentPage(pendingNavTarget);
    setPendingNavTarget(null);
  }, [pendingNavTarget]);

  const cancelNav = useCallback(() => setPendingNavTarget(null), []);

  const reportSiteProgress = useCallback((assigned: number, total: number) => {
    setSitesAssigned(assigned);
    setSitesTotal(total);
  }, []);

  const confirmGroups = useCallback(() => setGroupsConfirmed(true), []);
  const recordTestDeployment = useCallback((date: Date) => setTestConductedDate(date), []);
  const updateTestStats = useCallback((running: number, completed: number) => {
    setTestStats({ running, completed });
  }, []);

  const resetProgress = useCallback(() => {
    setSitesAssigned(0);
    setSitesTotal(0);
    setGroupsConfirmed(false);
    setTestConductedDate(null);
    setDeploymentScheduled(false);
    setDeploymentDate(null);
    setTestStats({ running: 0, completed: 0 });
  }, []);

  const scheduleDeployment = useCallback((date: Date) => {
    setDeploymentDate(date);
    setDeploymentScheduled(true);
  }, []);

  return (
    <WizardContext.Provider value={{
      currentPage, sitesAssigned, sitesTotal, groupsConfirmed,
      testConductedDate, deploymentScheduled, deploymentDate,
      pendingNavTarget, testStats, navigateTo, setNavGuard, proceedNav, cancelNav,
      reportSiteProgress, confirmGroups, recordTestDeployment, scheduleDeployment, updateTestStats, resetProgress,
    }}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = (): WizardContextType => {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used within WizardProvider');
  return ctx;
};
